import React, { useEffect, useId, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { GET_TINYCMS_PAGES, GET_TINYCMS_SITE } from '../../graphql';
import { CREATE_TINYCMS_PAGE } from '../../graphql/mutations';
import { showInkToast } from './InkToast';
import {
  PAGE_STARTS,
  PATH_HELP,
  cleanTypedPath,
  madeHere,
  newPageProblems,
  pathFromTitle,
  type PageStart,
} from './newPage';

/**
 * Content → Pages → New page ▾ → Build it myself (designer's notes,
 * 2026-10-25): a title, an address filled in from it until the person types
 * their own, and a starting layout. Create draft page makes an ordinary draft;
 * readers can't see it until it's published. "Ask your agent instead" swaps
 * this for the Ask your agent modal.
 */
export function NewPageDialog({
  onClose,
  onCreated,
  onAskAgent,
}: {
  onClose: () => void;
  onCreated: (pageId: string) => void;
  onAskAgent: () => void;
}) {
  const { accountId } = useAccount();
  const titleId = useId();
  const [title, setTitle] = useState('');
  const [typedPath, setTypedPath] = useState<string | null>(null);
  const [start, setStart] = useState<PageStart>('text');
  const [tried, setTried] = useState(false);
  const [serverPathError, setServerPathError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { data: pagesData } = useQuery(GET_TINYCMS_PAGES, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-first',
  });
  const { data: siteData } = useQuery(GET_TINYCMS_SITE, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-first',
  });
  const [createPage, { loading: creating }] = useMutation(CREATE_TINYCMS_PAGE, {
    refetchQueries: [{ query: GET_TINYCMS_PAGES, variables: { accountId } }],
    awaitRefetchQueries: true,
  });

  const path = typedPath ?? pathFromTitle(title);
  const taken: string[] = (pagesData?.tinycmsPages || []).map((p: { path: string }) => p.path);
  const problems = newPageProblems(title, path, taken);
  const titleError = tried ? problems.title : null;
  const pathError = (tried ? problems.path : null) || serverPathError;
  const site = String(siteData?.tinycmsSite?.siteUrl || '').replace(/^https?:\/\//, '');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const create = async () => {
    setTried(true);
    setError(null);
    setServerPathError(null);
    if (!accountId || problems.title || problems.path) return;
    try {
      const { data } = await createPage({
        variables: { accountId, title: title.trim(), path, start },
      });
      const page = data?.createTinycmsPage;
      madeHere.add(page.id);
      showInkToast('Draft page created. Readers can’t see it until you publish it.');
      onCreated(page.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Couldn’t create the page';
      // The address is the problem: say so under it (in use, built in, no group page).
      if (/already|address|parent|built-in|lowercase/i.test(message)) setServerPathError(message);
      else setError(message);
    }
  };

  return (
    <div
      className="ink-modal-back af-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="ink-modal af-modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="af-head">
          <div className="af-title" id={titleId}>
            New page
          </div>
          <p className="af-sub">It starts as a draft. Readers can’t see it until you publish it.</p>
        </div>
        <form
          className="np-body"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          <label className="af-field">
            Title
            <input
              className="ink-input"
              value={title}
              placeholder="Title"
              autoFocus
              data-bad={titleError ? 'true' : undefined}
              aria-invalid={titleError ? true : undefined}
              onChange={(e) => setTitle(e.target.value)}
            />
            {titleError ? <span className="np-error">{titleError}</span> : null}
          </label>
          <label className="af-field">
            Address
            <span className="np-address" data-bad={pathError ? 'true' : undefined}>
              <span className="np-address-site" title={site ? `${site}/` : undefined}>
                {site ? `${site}/` : '/'}
              </span>
              <input
                value={path}
                placeholder="my-page"
                spellCheck={false}
                autoCapitalize="none"
                aria-invalid={pathError ? true : undefined}
                onChange={(e) => {
                  setServerPathError(null);
                  setTypedPath(cleanTypedPath(e.target.value));
                }}
              />
            </span>
            <span className="np-help" data-bad={pathError ? 'true' : undefined}>
              {pathError || PATH_HELP}
            </span>
          </label>
          <div className="af-field" role="radiogroup" aria-label="Start with">
            Start with
            <div className="np-starts">
              {PAGE_STARTS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  role="radio"
                  aria-checked={start === s.id}
                  className="np-start"
                  onClick={() => setStart(s.id)}
                >
                  <span className="np-radio" aria-hidden="true" />
                  <span>
                    <span className="np-start-label">{s.label}</span>
                    <span className="np-start-sub">{s.sub}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
          {/* Enter in a field creates the page. */}
          <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>
        <div className="af-foot">
          <button type="button" className="np-ask" onClick={onAskAgent}>
            Ask your agent instead
          </button>
          {error ? (
            <span className="af-failed" role="alert">
              {error}
            </span>
          ) : null}
          <button type="button" className="ink-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ink-btn ink-btn-primary"
            disabled={creating}
            onClick={() => void create()}
          >
            {creating ? 'Creating…' : 'Create draft page'}
          </button>
        </div>
      </div>
    </div>
  );
}
